import { getFirestore, collection, addDoc, Timestamp, doc } from 'firebase/firestore';
import React, { useMemo, useState } from 'react';
import PrimaryButton from '../components/buttons/PrimaryButton';
import Input from '../components/inputs/Input';
import { app } from '../firebase'; // Importez votre configuration Firebase
import { isCoownership, isRentalManager, useUser } from '../contexts/UserContext';
import TextareaInput from '../components/inputs/TextareaInput';
import CheckboxInput from '../components/inputs/CheckboxInput';
import DateTimeInput from '../components/inputs/DateTimeInput';
import Label from '../components/texts/Label';
import FilesInput from '../components/inputs/FilesInput';
import SelectInput from '../components/inputs/SelectInput';
import { handleUpload } from '../librairies/FirebaseStorage';
import {
  buildDaySlots,
  buildDayWindowBlocks,
  durationValueToMinutes,
  extractTimePart,
  formatMultiDaySlotTitle,
  formatSlotDayLines,
  getDayKeysInRange,
  getLocalDayKey,
  getSlotCapacity,
  normalizeDateTimeToStep,
  toStoredCreneauSlot,
} from './appointmentSlots';


const db = getFirestore(app);

function AddSurvey({onClose, date}) {

  //contexte de l'utilisateur connecté
  const user = useUser();
  const [title, setTitle] = useState("");
  const [complementaryInfo, setComplementaryInfo] = useState("");
  const [responses, setResponses] = useState([{ text: "" }]);
  const [expirationDate, setExpirationDate] = useState("");
  const [surveyType, setSurveyType] = useState("survey");
  const [surveyMultiple, setSurveyMultiple] = useState(false);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState("");
  const [coownershipComiteeTarget, setCoownershipComiteeTarget] = useState(false); 
  const [coownerTarget, setCoownerTarget] = useState(false); 
  const [tenantTarget, setTenantTarget] = useState(isRentalManager(user.user) ? true : false); 
  const [files, setFiles] = useState([]);
  const [interventionDuration, setInterventionDuration] = useState(30);
  const [durationUnit, setDurationUnit] = useState("minutes");
  const [firstInterventionDate, setFirstInterventionDate] = useState("");
  const [lastInterventionDate, setLastInterventionDate] = useState("");
  const [dayStartTimes, setDayStartTimes] = useState({});
  const [dayWindows, setDayWindows] = useState({});
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [selectedSlotDay, setSelectedSlotDay] = useState("");
  const [receiveEmail, setReceiveEmail] = useState(true);

  const handleAddResponse = () => {
    setResponses([...responses, { text: ""}]);
  };

  const handleResponseChange = (index, value) => {
    const newResponses = [...responses];
    newResponses[index].text = value;
    setResponses(newResponses);
  };

  const handleDeleteResponse = (index) => {
    if (responses.length > 1) {
      setResponses(responses.filter((_, i) => i !== index));
    }
  };

  /**
   * Modification des fichiers
   * @param {*} e 
   */
  const handleFilesChange = (e) => {
    console.log("e.target.files",e.target.files);
    if (e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files); // Convertir FileList en tableau
      setFiles(prevFiles => [...prevFiles, ...selectedFiles]); // Ajouter au state
    }
  };

  const handleRemoveFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const clearSelectedSlots = () => {
    setSelectedSlots([]);
    setSelectedSlotDay("");
  };

  const handleInterventionDurationChange = (value) => {
    setInterventionDuration(value);
    clearSelectedSlots();
  };

  const handleDurationUnitChange = (unit) => {
    setDurationUnit(unit);
    if (unit === "minutes") setInterventionDuration(30);
    else setInterventionDuration(1);
    clearSelectedSlots();
  };

  const dayKeysInRange = useMemo(
    () => getDayKeysInRange(firstInterventionDate, lastInterventionDate),
    [firstInterventionDate, lastInterventionDate]
  );

  const normalizedDayStartTimes = useMemo(() => {
    const defaultStartTime = extractTimePart(firstInterventionDate);
    const next = {};
    dayKeysInRange.forEach((dayKey) => {
      next[dayKey] = dayStartTimes[dayKey] || defaultStartTime;
    });
    return next;
  }, [dayKeysInRange, dayStartTimes, firstInterventionDate]);

  const handleDayStartTimeChange = (dayKey, value) => {
    setDayStartTimes((prev) => ({
      ...prev,
      [dayKey]: value,
    }));
  };

  const normalizedDayWindows = useMemo(() => {
    const next = {};
    dayKeysInRange.forEach((dayKey) => {
      next[dayKey] = {
        start: dayWindows[dayKey]?.start || "09:00",
        end: dayWindows[dayKey]?.end || "17:00",
      };
    });
    return next;
  }, [dayKeysInRange, dayWindows]);

  const handleDayWindowChange = (dayKey, field, value) => {
    setDayWindows((prev) => ({
      ...prev,
      [dayKey]: {
        start: prev[dayKey]?.start || normalizedDayWindows[dayKey]?.start || "09:00",
        end: prev[dayKey]?.end || normalizedDayWindows[dayKey]?.end || "17:00",
        [field]: value,
      },
    }));
  };

  const durationMinutes = durationValueToMinutes(interventionDuration, durationUnit);

  const generatedSlots = useMemo(() => {
    if (surveyType !== "appointment") {
      return [];
    }
    if (durationUnit === "days") {
      return buildDayWindowBlocks(dayKeysInRange, normalizedDayWindows, Number(interventionDuration));
    }
    return dayKeysInRange.flatMap((dayKey) =>
      buildDaySlots(dayKey, normalizedDayStartTimes[dayKey], durationMinutes)
    );
  }, [
    surveyType,
    durationUnit,
    interventionDuration,
    dayKeysInRange,
    normalizedDayStartTimes,
    normalizedDayWindows,
    durationMinutes,
  ]);

  const selectedSlotIds = useMemo(
    () => new Set(selectedSlots.map((slot) => String(slot.id))),
    [selectedSlots]
  );

  const selectableGridSlots = generatedSlots;

  const slotsByDay = useMemo(() => {
    return selectableGridSlots.reduce((acc, slot) => {
      if (!acc[slot.dayKey]) {
        acc[slot.dayKey] = {
          label: slot.dayLabel,
          slots: []
        };
      }
      acc[slot.dayKey].slots.push(slot);
      return acc;
    }, {});
  }, [selectableGridSlots]);

  const dayKeys = Object.keys(slotsByDay);
  const currentDayKey = selectedSlotDay && slotsByDay[selectedSlotDay] ? selectedSlotDay : (dayKeys[0] || "");
  const selectedDayKeys = useMemo(() => {
    return new Set(
      selectedSlots.map((slot) => getLocalDayKey(new Date(slot.startTs)))
    );
  }, [selectedSlots]);

  const orderedSelectedSlots = useMemo(
    () => [...selectedSlots].sort((a, b) => a.startTs - b.startTs),
    [selectedSlots]
  );

  const addSlot = (slot) => {
    if (selectedSlotIds.has(String(slot.id))) {
      setError("");
      setSelectedSlots((prev) =>
        prev.map((item) =>
          String(item.id) === String(slot.id)
            ? { ...item, capacity: getSlotCapacity(item) + 1 }
            : item
        )
      );
      return;
    }
    setError("");
    setSelectedSlots((prev) => [
      ...prev,
      {
        id: slot.id,
        text: slot.text,
        startTs: slot.startTs,
        endTs: slot.endTs,
        capacity: 1,
        ...(slot.days ? { days: slot.days } : {}),
      },
    ]);
  };

  const decreaseSelectedSlot = (slotId) => {
    setSelectedSlots((prev) =>
      prev.flatMap((slot) => {
        if (String(slot.id) !== String(slotId)) return [slot];
        const nextCapacity = getSlotCapacity(slot) - 1;
        if (nextCapacity < 1) return [];
        return [{ ...slot, capacity: nextCapacity }];
      })
    );
  };

  const removeSelectedSlot = (slotId) => {
    setSelectedSlots((prev) => prev.filter((slot) => String(slot.id) !== String(slotId)));
  };

  const capacityForSlot = (slotId) => {
    const slot = selectedSlots.find((item) => String(item.id) === String(slotId));
    return slot ? getSlotCapacity(slot) : 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title){
      setError("Le titre est requis.");
      return;
    } 
    if (!expirationDate){
      setError("La date d'expiration est requise.");
      return;
    } 
    if (surveyType === "survey" && responses.some(resp => !resp.text)) {
      setError("Toutes les réponses doivent être remplies.");
      return;
    }
    if (surveyType === "appointment") {
      if (!firstInterventionDate || !lastInterventionDate) {
        setError("Les dates de première et dernière intervention sont requises.");
        return;
      }
      if (!Number.isInteger(Number(interventionDuration)) || durationMinutes <= 0) {
        setError("La durée d'intervention doit être un nombre entier supérieur à 0.");
        return;
      }
      if (firstInterventionDate > lastInterventionDate) {
        setError("La première intervention doit être avant ou égale à la dernière intervention.");
        return;
      }
      if (durationUnit !== "days" && dayKeysInRange.some((dayKey) => !normalizedDayStartTimes[dayKey])) {
        setError("Vous devez renseigner une heure de début pour chaque jour.");
        return;
      }
      if (generatedSlots.length === 0) {
        setError(
          durationUnit === "days"
            ? "Chaque jour du créneau doit avoir une heure de fin après l'heure de début, et la durée doit tenir dans la plage."
            : "Cette durée ne tient pas dans la journée à partir de l'heure de début."
        );
        return;
      }
      if (selectedSlots.length === 0) {
        setError("Vous devez sélectionner au moins un créneau.");
        return;
      }
    }
    if (!coownerTarget && !coownershipComiteeTarget && !tenantTarget) {
      setError("Vous devez sélectionner au minimum une cible pour le sondage"); 
      return; 
    }

    setLoader(true);

    let uploadedUrls = [];
    if(files.length > 0){
      for (const file of files) {
        let url = await handleUpload("surveys/", file);
        uploadedUrls.push(url);
      }
    }

    try {
      const surveyRef = collection(db, `${user.user.userData.lastCoownershipLoaded.path}/surveys`);

      let type = surveyType === "survey" ? (surveyMultiple ? "multiple" :  "simple") : "creneau";
      const selectedSlotResponses = [...selectedSlots]
        .sort((a, b) => a.startTs - b.startTs)
        .map((slot) => toStoredCreneauSlot(slot));

      await addDoc(surveyRef, {
        title,
        complementaryInfo: complementaryInfo.trim() || null,
        responses: surveyType === "appointment" ? selectedSlotResponses : responses,
        dateEnd: Timestamp.fromDate(new Date(expirationDate)),
        coownerTarget,
        coownershipComiteeTarget,
        tenantTarget,
        type: type, //simple ; creneau ; multiple
        idCreator: doc(db, "users/"+user.user.user.uid),
        dateSave: Timestamp.now(),
        status: "accepted",
        createdBy: isCoownership(user.user) ? "le gestionnaire" : ("le prestataire "+user.user.userData.company),
        statusCreator: user.user.userData.status, //copropriété ou prestataire
        files:uploadedUrls,
        interventionDuration: surveyType === "appointment" ? durationMinutes : null,
        interventionDurationUnit: surveyType === "appointment" ? durationUnit : null,
        receiveEmail: surveyType === "appointment" ? receiveEmail : false,
      });

      onClose();
    } catch (err) {
      console.log(err)
      setError("Erreur lors de l'enregistrement du sondage.");
    }

    setLoader(false);
  };

  return (
    <div id="authentication-modal" tabindex="-1" aria-hidden="true" className="mx-auto w-full fixed inset-0 flex items-center justify-center overflow-auto">
      <div className="relative w-full max-w-2xl max-h-full">
        <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
          <button type="button" onClick={onClose} className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white" data-modal-hide="authentication-modal">
            <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
            </svg>
            <span className="sr-only">Close modal</span>
          </button>
          <div className="px-6 py-6 lg:px-8">
            <h3 className="mb-4 text-xl font-medium text-gray-900 dark:text-white">Ajouter un sondage</h3>

            <form onSubmit={handleSubmit}>

              <div className='mb-6'>

                <div className='border-b border-gray mt-0.5 mb-2'></div>


                <div className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setSurveyType("survey")}
                      className={`w-full rounded-xl border p-4 text-left transition ${
                        surveyType === "survey"
                          ? "border-secondary bg-secondary/10"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="surveyType"
                        value="survey"
                        checked={surveyType === "survey"}
                        onChange={() => setSurveyType("survey")}
                        className="sr-only"
                      />
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex gap-3">
                          <div className="text-2xl">📋</div>
                          <div>
                            <p className="font-semibold text-gray-900">Sondage</p>
                            <p className="text-sm text-gray-600">
                              Recueillez des avis et validez vos décisions en quelques clics (travaux, devis, préférences…).
                            </p>
                          </div>
                        </div>
                        <div
                          className={`mt-1 inline-flex h-6 w-8 shrink-0 items-center justify-center rounded ${
                            surveyType === "survey"
                              ? "bg-secondary text-white"
                              : "bg-gray-100 text-transparent"
                          }`}
                        >
                          ✓
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSurveyType("appointment")}
                      className={`w-full rounded-xl border p-4 text-left transition ${
                        surveyType === "appointment"
                          ? "border-secondary bg-secondary/10"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="surveyType"
                        value="appointment"
                        checked={surveyType === "appointment"}
                        onChange={() => setSurveyType("appointment")}
                        className="sr-only"
                      />
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex gap-3">
                          <div className="text-2xl">📅</div>
                          <div>
                            <p className="font-semibold text-gray-900">Rendez-vous</p>
                            <p className="text-sm text-gray-600">
                              Planifier une intervention ou une visite dans les logements privatifs (créneaux proposés automatiquement)
                            </p>
                          </div>
                        </div>
                        <div
                          className={`mt-1 inline-flex h-6 w-8 shrink-0 items-center justify-center rounded ${
                            surveyType === "appointment"
                              ? "bg-secondary text-white"
                              : "bg-gray-100 text-transparent"
                          }`}
                        >
                          ✓
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <Input id={"title"} label={"Question"} type={"text"} name={"title"} value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>

                {surveyType === "survey" ? (
                  <div className="mt-4">
                    <Label text={"Options de réponse"} required={true} />
                    {responses.map((response, index) => (
                      <div key={index} className="flex items-center gap-2 mb-2">
                        <input type="text" value={response.text} onChange={(e) => handleResponseChange(index, e.target.value)} className="border p-2 rounded flex-grow" />
                        {responses.length > 1 && (
                          <div className='w-5 h-5 rounded-full bg-red-500 flex items-center justify-center'>
                            <button type="button" onClick={() => handleDeleteResponse(index)} className=" text-white text-3xl font-bold mb-1">
                              -
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={handleAddResponse} className="text-blue-500 text-sm">+ Ajouter une réponse</button>
                    <div className='mt-2'>
                      <CheckboxInput  id={"surveyTypeMultiple"} name={"surveyTypeMultiple"} label={"Autoriser plusieurs réponses"} checked={surveyMultiple} onChange={() => setSurveyMultiple(!surveyMultiple)} />
                    </div>
                  </div>
                ) : (
                  <div className="mt-4">
                    <Label text={"Proposer des créneaux disponibles"} required={true} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        id={"interventionDuration"}
                        label={"Durée"}
                        type={"number"}
                        name={"interventionDuration"}
                        value={interventionDuration}
                        onChange={(e) => handleInterventionDurationChange(e.target.value)}
                      />
                      <SelectInput
                        id={"durationUnit"}
                        name={"durationUnit"}
                        label={"Unité"}
                        value={durationUnit}
                        onChange={(e) => handleDurationUnitChange(e.target.value)}
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Heures</option>
                        <option value="days">Jours</option>
                      </SelectInput>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <DateTimeInput
                        id={"firstInterventionDate"}
                        label={"Première intervention"}
                        value={firstInterventionDate}
                        onChange={setFirstInterventionDate}
                        dateOnly
                      />
                      <DateTimeInput
                        id={"lastInterventionDate"}
                        label={"Dernière intervention"}
                        value={lastInterventionDate}
                        onChange={setLastInterventionDate}
                        dateOnly
                      />
                    </div>
                    <p className="text-xs text-gray-500 -mt-2 mb-2">
                      Changer la plage de dates ne retire pas les créneaux déjà choisis.
                      Recliquez sur un créneau pour augmenter le nombre de personnes qui peuvent le réserver.
                      {durationUnit === "days"
                        ? " Un créneau de plusieurs jours se réserve en entier."
                        : ""}
                    </p>

                    {durationUnit === "days" && dayKeysInRange.length > 0 && (
                      <div className="mt-1 rounded-lg border border-gray-200 p-3">
                        <p className="mb-2 text-sm font-medium text-gray-700">Horaires de chaque jour</p>
                        <div className="grid grid-cols-1 gap-3">
                          {dayKeysInRange.map((dayKey) => (
                            <div key={dayKey} className="rounded-md border border-gray-100 bg-gray-50 p-2">
                              <p className="mb-2 text-sm font-medium text-gray-700">
                                {new Intl.DateTimeFormat("fr-FR", {
                                  weekday: "long",
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                }).format(new Date(`${dayKey}T00:00:00`))}
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                <label className="text-xs text-gray-600">
                                  Début
                                  <input
                                    type="time"
                                    value={normalizedDayWindows[dayKey].start}
                                    step={300}
                                    onChange={(e) => handleDayWindowChange(dayKey, "start", e.target.value)}
                                    className="mt-1 w-full px-2 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                                  />
                                </label>
                                <label className="text-xs text-gray-600">
                                  Fin
                                  <input
                                    type="time"
                                    value={normalizedDayWindows[dayKey].end}
                                    step={300}
                                    onChange={(e) => handleDayWindowChange(dayKey, "end", e.target.value)}
                                    className="mt-1 w-full px-2 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                                  />
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {durationUnit !== "days" && dayKeysInRange.length > 0 && (
                      <div className="mt-1 rounded-lg border border-gray-200 p-3">
                        <p className="mb-2 text-sm font-medium text-gray-700">Heure de début par jour</p>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          {dayKeysInRange.map((dayKey) => (
                            <div key={dayKey} className="rounded-md border border-gray-100 bg-gray-50 p-2">
                              <p className="mb-1 text-sm font-medium text-gray-700">
                                {new Intl.DateTimeFormat("fr-FR", {
                                  weekday: "long",
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                }).format(new Date(`${dayKey}T00:00:00`))}
                              </p>
                              <input
                                type="time"
                                value={normalizedDayStartTimes[dayKey]}
                                step={300}
                                onChange={(e) => handleDayStartTimeChange(dayKey, e.target.value)}
                                className="w-full px-2 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {generatedSlots.length > 0 ? (
                      <div className="mt-3 rounded-lg border border-gray-200 p-3">
                        {durationUnit !== "days" && (
                        <div className="mb-3 flex flex-wrap gap-2">
                          {dayKeys.map((dayKey) => (
                            <button
                              key={dayKey}
                              type="button"
                              onClick={() => setSelectedSlotDay(dayKey)}
                              className={`rounded-md px-3 py-1 text-sm ${
                                currentDayKey === dayKey
                                  ? "bg-secondary text-white"
                                  : selectedDayKeys.has(dayKey)
                                  ? "bg-primary text-white"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                            >
                              {slotsByDay[dayKey].label}
                            </button>
                          ))}
                        </div>
                        )}

                        {durationUnit === "days" ? (
                          <div className="flex flex-col gap-2">
                            {generatedSlots.map((slot) => {
                              const capacity = capacityForSlot(slot.id);
                              return (
                                <button
                                  key={slot.id}
                                  type="button"
                                  onClick={() => addSlot(slot)}
                                  className={`rounded-lg px-3 py-2 text-left text-sm ${
                                    capacity > 0
                                      ? "bg-secondary/20 text-black font-bold border border-secondary"
                                      : "bg-gray-100 text-gray-700 border border-transparent hover:bg-gray-200"
                                  }`}
                                >
                                  <span>{(slot.days?.length > 1 ? formatMultiDaySlotTitle(slot.days) : slot.text)}{capacity > 0 ? ` · ×${capacity}` : ""}</span>
                                  {slot.days?.length > 1 && (
                                    <span className="mt-1 block text-xs font-normal text-gray-600">
                                      {formatSlotDayLines(slot.days).join(" · ")}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                        <div className="flex flex-wrap gap-2">
                          {(slotsByDay[currentDayKey]?.slots || []).map((slot) => {
                            const capacity = capacityForSlot(slot.id);
                            return (
                            <button
                              key={slot.id}
                              type="button"
                              onClick={() => addSlot(slot)}
                              className={`rounded-lg px-3 py-1.5 text-sm ${
                                capacity > 0
                                  ? "bg-secondary/20 text-black font-bold border border-secondary"
                                  : "bg-gray-100 text-gray-700 border border-transparent hover:bg-gray-200"
                              }`}
                            >
                              {slot.timeLabel}{capacity > 0 ? ` · ×${capacity}` : ""}
                            </button>
                            );
                          })}
                        </div>
                        )}
                      </div>
                    ) : (
                        <p className="mt-2 text-sm text-gray-500">
                        {dayKeysInRange.length === 0
                          ? "Renseignez la durée et la plage de dates pour générer les créneaux."
                          : durationUnit === "days"
                            ? "Chaque jour du créneau doit avoir une heure de fin après l'heure de début, et la durée doit tenir dans la plage."
                            : "Cette durée ne tient pas dans la journée à partir de l'heure de début."}
                      </p>
                    )}

                    <p className="mt-3 text-xs text-gray-500">
                      <span className="font-bold text-gray-700">
                        {selectedSlots.length} créneau{selectedSlots.length > 1 ? "x" : ""} sélectionné{selectedSlots.length > 1 ? "s" : ""}
                      </span>
                    </p>

                    <div className="mt-2 max-h-48 space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2">
                      {orderedSelectedSlots.length === 0 ? (
                        <p className="text-sm text-gray-500">Aucun créneau sélectionné.</p>
                      ) : (
                        orderedSelectedSlots.map((slot, index) => (
                          <div
                            key={`${slot.id}-${index}`}
                            className="flex items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-800"
                          >
                            <span>
                              {slot.days?.length > 1 ? formatMultiDaySlotTitle(slot.days) : slot.text}
                              {` · ${getSlotCapacity(slot)} place${getSlotCapacity(slot) > 1 ? "s" : ""}`}
                              {slot.days?.length > 1 && (
                                <span className="mt-1 block text-xs font-normal text-gray-600">
                                  {formatSlotDayLines(slot.days).join(" · ")}
                                </span>
                              )}
                            </span>
                            <span className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => decreaseSelectedSlot(slot.id)}
                                className="font-medium text-gray-700 hover:underline"
                              >
                                −1
                              </button>
                              <button
                                type="button"
                                onClick={() => removeSelectedSlot(slot.id)}
                                className="font-medium text-red-600 hover:underline"
                              >
                                Supprimer
                              </button>
                            </span>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="mt-2">
                      <CheckboxInput
                        id="receiveEmail"
                        name="receiveEmail"
                        label="Recevoir les notifications par e-mail"
                        checked={receiveEmail}
                        onChange={() => setReceiveEmail(!receiveEmail)}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <Label text={"Sondage destiné à"} required={true} />
                <div className='flex justify-between'>
                  <CheckboxInput  id={"coownershipComiteeTarget"} name={"coownershipComiteeTarget"} label={"CS"} checked={coownershipComiteeTarget} onChange={() => setCoownershipComiteeTarget(!coownershipComiteeTarget)} disabled={isRentalManager(user.user)} />
                  <CheckboxInput  id={"coownerTarget"} name={"coownerTarget"} label={"Copropriétaires"} checked={coownerTarget} onChange={() => setCoownerTarget(!coownerTarget)} disabled={isRentalManager(user.user)} />
                  <CheckboxInput  id={"tenantTarget"} name={"tenantTarget"} label={"Locataires"} checked={tenantTarget} onChange={() => setTenantTarget(!tenantTarget)} disabled={isRentalManager(user.user)} />
                </div>
              </div>

              <div className="mt-4">
                <DateTimeInput id={"expirationDate"} label={"Date d'expiration"} value={expirationDate} onChange={(value) => setExpirationDate(normalizeDateTimeToStep(value, 5))} stepMinutes={5} />
              </div>

              <div className="mt-4">
                <TextareaInput
                  id="complementaryInfo"
                  name="complementaryInfo"
                  label="Information complémentaire"
                  value={complementaryInfo}
                  onChange={(e) => setComplementaryInfo(e.target.value)}
                  required={false}
                />
                <p className="text-xs text-gray-500 -mt-1 mb-2">
                  Facultatif. Si ce sondage concerne uniquement une partie des occupants (ex : bâtiment A, rez-de-chaussée…), indiquez-le ici. Cela permet de vérifier que seules les personnes concernées peuvent répondre. Exemple : ce sondage est destiné aux occupants du bâtiment A.
                </p>
              </div>

              <div className="mt-4">
                <FilesInput id={"affichage"} name={"affichage"} label={"Images ou PDF"} files={files} onChange={handleFilesChange} onRemoveFile={handleRemoveFile} fileAccepted={"image/*,application/pdf"} />
              </div>

              <div className="mt-4 text-center">
                <PrimaryButton type={"submit"} value={"Ajouter"} loading={loader} />
              </div>

              {error !== "" ? (
                <p className='justify-center flex my-1 text-red-500'>{error}</p>
              ) : null}
            
            </form>

          </div>
        </div>
      </div>
    </div> 
  );
}

export default AddSurvey;